import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface WelcomeAnimationProps {
  show: boolean;
  userName: string;
  greeting: string;
  onComplete: () => void;
}

export const WelcomeAnimation: React.FC<WelcomeAnimationProps> = ({
  show,
  userName,
  greeting,
  onComplete
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      
      // Auto-hide nach 4 Sekunden
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onComplete, 500); // Warte auf Animation bevor onComplete
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 30,
            duration: 0.6
          }}
          className="fixed top-16 left-0 right-0 z-40 pointer-events-none"
        >
          <div className="container mx-auto px-4 py-2">
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 border border-emerald-200 rounded-2xl shadow-lg backdrop-blur-sm mx-auto max-w-md"
            >
              <div className="px-6 py-4 text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ 
                    delay: 0.4, 
                    duration: 0.5,
                    ease: "easeOut"
                  }}
                  className="text-2xl mb-2 select-none"
                  style={{ 
                    display: 'inline-block',
                    lineHeight: 1,
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                >
                  👋
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                  className="text-lg font-semibold text-gray-900 mb-1"
                >
                  Willkommen zurück, {userName}!
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.4 }}
                  className="text-sm text-emerald-700 font-medium"
                >
                  {greeting} Schön, dass du da bist!
                </motion.p>
              </div>
              
              {/* Eleganter Fortschrittsbalken */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1, duration: 3, ease: "linear" }}
                className="h-1 bg-gradient-to-r from-emerald-400 to-green-400 rounded-b-2xl origin-left"
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 